import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mail,
  Phone,
  ExternalLink,
  BadgeCheck,
  Calendar,
  Globe,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    role="img"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12.05 21.784h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.208c0-5.448 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.451-4.438 9.833-9.885 9.833z" />
  </svg>
);

const LinkedinIcon = ({ className }: { className?: string }) => (
  <svg
    role="img"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
  </svg>
);

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg
    role="img"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const links = [
  {
    label: "Book A Demo",
    description: "See Clean Marketing in action",
    href: "/book-demo",
    internal: true,
    highlight: true,
    icon: "calendar",
  },
  {
    label: "Visit Clean Marketing",
    description: "www.cleanmarketing.co.uk",
    href: "https://www.cleanmarketing.co.uk",
    internal: false,
    icon: "globe",
  },
  {
    label: "Connect on WhatsApp",
    description: "Message me directly",
    href: "https://wa.me/message/SUKTTZPF2REXP1",
    internal: false,
    icon: "whatsapp",
  },
  {
    label: "LinkedIn",
    description: "linkedin.com/in/jsavory",
    href: "https://www.linkedin.com/in/jsavory",
    internal: false,
    icon: "linkedin",
  },
  {
    label: "Add Me On Facebook",
    description: "Let's connect personally",
    href: "https://www.facebook.com/share/1DNGKtJDu8/",
    internal: false,
    icon: "facebook",
  },
  {
    label: "Follow Clean Marketing",
    description: "Stay up to date with our latest",
    href: "https://www.facebook.com/ukcleanmarketing",
    internal: false,
    icon: "facebook",
  },
];

const formatUkPhone = (e164: string) => {
  const digits = e164.replace("+44", "0");
  if (digits.length === 11) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return digits;
};

const phones = [{ raw: "+447700140011" }, { raw: "+441225234456" }];

const James = () => {
  return (
    <div className="min-h-screen bg-secondary font-sans text-white flex flex-col items-center px-4 py-12 sm:py-16">
      <SEOHead
        title="James Savory | Window Cleaning Marketing Specialist"
        description="Connect with James Savory, Window Cleaning Marketing Specialist and founder of Clean Marketing. Book a demo, connect on WhatsApp, LinkedIn, or Facebook."
        canonical="/james"
        ogImage="https://vibe.filesafe.space/1775166810156919871/attachments/f3b9ad41-8d10-4e2f-80ac-b56da7dcced9.png"
      />
      <div className="w-full max-w-md mx-auto flex flex-col items-center">
        {/* Photo */}
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-full bg-primary/30 blur-xl scale-110"></div>
          <img
            src="https://vibe.filesafe.space/1775166810156919871/attachments/f3b9ad41-8d10-4e2f-80ac-b56da7dcced9.png"
            alt="James Savory, Window Cleaning Marketing Specialist"
            width="160"
            height="160"
            className="relative h-32 w-32 sm:h-40 sm:w-40 rounded-full object-cover border-4 border-white shadow-xl"
          />
        </div>

        {/* Name & Title */}
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white text-center">
          James Savory
        </h1>
        <p className="mt-2 text-lg sm:text-xl font-bold text-primary text-center max-w-[16rem] leading-snug">
          Window Cleaning Marketing Specialist
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs sm:text-sm font-medium text-blue-100 backdrop-blur-sm">
          <BadgeCheck className="h-4 w-4 text-primary" />
          HighLevel Certified Admin
        </div>

        {/* Links */}
        <nav aria-label="James Savory links" className="w-full mt-10 space-y-4">
          {links.map((link) => {
            const content = (
              <>
                <span className="flex items-center gap-3">
                  {link.icon === "calendar" && (
                    <Calendar className="h-5 w-5 shrink-0 text-white" />
                  )}
                  {link.icon === "globe" && (
                    <Globe className="h-5 w-5 shrink-0 text-white" />
                  )}
                  {link.icon === "whatsapp" && (
                    <WhatsAppIcon className="h-5 w-5 shrink-0 text-white" />
                  )}
                  {link.icon === "linkedin" && (
                    <LinkedinIcon className="h-5 w-5 shrink-0 text-white" />
                  )}
                  {link.icon === "facebook" && (
                    <FacebookIcon className="h-5 w-5 shrink-0 text-white" />
                  )}
                  <span className="flex flex-col items-start text-left">
                    <span className="font-bold">{link.label}</span>
                    <span
                      className={`text-xs sm:text-sm ${
                        link.highlight ? "text-white/80" : "text-blue-100/80"
                      }`}
                    >
                      {link.description}
                    </span>
                  </span>
                </span>
                {link.internal ? (
                  <ArrowRight className="h-5 w-5 shrink-0" />
                ) : (
                  <ExternalLink className="h-5 w-5 shrink-0" />
                )}
              </>
            );

            const className = `w-full flex items-center justify-between rounded-2xl px-5 py-4 shadow-md transition-all hover:-translate-y-0.5 ${
              link.highlight
                ? "bg-primary text-primary-foreground hover:bg-[#e04825]"
                : "bg-white/10 text-white border border-white/15 hover:bg-white/20 backdrop-blur-sm"
            }`;

            return link.internal ? (
              <Link key={link.label} to={link.href} className={className}>
                {content}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
              >
                {content}
              </a>
            );
          })}
        </nav>

        {/* Direct contact */}
        <div className="w-full mt-8 pt-8 border-t border-white/15 space-y-3">
          <a
            href="mailto:james@cleanmarketing.co.uk"
            className="w-full flex items-center gap-3 rounded-2xl px-5 py-4 bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
          >
            <Mail className="h-5 w-5 shrink-0 text-primary" />
            <span className="text-sm font-medium">
              james@cleanmarketing.co.uk
            </span>
          </a>
          {phones.map((phone) => (
            <a
              key={phone.raw}
              href={`tel:${phone.raw}`}
              className="w-full flex items-center gap-3 rounded-2xl px-5 py-4 bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
            >
              <Phone className="h-5 w-5 shrink-0 text-primary" />
              <span className="text-sm font-medium">
                {formatUkPhone(phone.raw)}
              </span>
            </a>
          ))}
        </div>

        <p className="mt-10 text-xs text-blue-100/60 text-center">
          © {new Date().getFullYear()} Clean Marketing. Built by a window
          cleaner, for window cleaners.
        </p>
      </div>
    </div>
  );
};

export default James;
